// What the balance hero says, derived from `BalanceReturn`.
//
// One pure function, because this is where every trap in the billing
// domain has to be handled exactly once — and because a wrong answer
// here is a homeowner told they are up to date when they owe money, or
// chased for money they do not owe.

import { BalanceReturn } from '../clients/rust-api-client';
import {
  absMoney,
  isNegativeMoney,
  moneyToCents,
} from '../utils/billingHelpers';

export type AccountStatus =
  /** Balance below zero: the association holds money for them. */
  | 'credit'
  /** Balance is zero and dues are configured. */
  | 'paid_up'
  /** Owes money, nothing overdue yet. */
  | 'due'
  /** Owes money and it is past its due date. */
  | 'past_due'
  /** No assessment schedule is in force for this home. */
  | 'unconfigured';

export interface AccountState {
  status: AccountStatus;
  /**
   * The figure the hero shows, unsigned. A credit reads "$40.00
   * credit", never "-$40.00" — the minus sign in front of a balance is
   * how somebody concludes they owe money they do not.
   */
  amount: string;
  /** The one-line explanation under the number. */
  headline: string;
  /** True where the number should read as owed rather than held. */
  owes: boolean;
  daysPastDue: number;
}

const BIG_ZERO = BigInt(0);

/**
 * Turn a balance into what the screen says.
 *
 * The three rules that are easy to get wrong, in the order they are
 * checked:
 *
 * 1. **`current_period_amount === null` means no schedule is in force,
 *    which is not the same as billing zero.** An association that
 *    bills nothing recurring is unconfigured, and rendering `$0.00`
 *    for both tells a homeowner they are up to date when nobody has
 *    set their dues up. It is checked first, but only when the balance
 *    is also zero — a home with a real balance has real charges on it
 *    whatever the schedule says, and calling that "unconfigured" would
 *    hide money owed.
 *
 * 2. **A negative balance is a credit, not a debt.** Only some HOAs
 *    allow it (`allow_credit_balances`, default off — see
 *    `knowledge/notes/decision-credit-balances-per-hoa.md`), so it is
 *    uncommon and correspondingly easy to leave untested.
 *
 * 3. **`days_past_due` decides overdue, not the balance's sign.** A
 *    positive balance that is not yet due is not late.
 */
export const accountState = (balance?: BalanceReturn | null): AccountState => {
  if (!balance) {
    return {
      status: 'unconfigured',
      amount: '0.00',
      headline: "We couldn't load your balance.",
      owes: false,
      daysPastDue: 0,
    };
  }

  const cents = moneyToCents(balance.balance);
  const daysPastDue = balance.days_past_due ?? 0;

  // Rule 1 — nothing owed and nothing configured to owe.
  if (cents === BIG_ZERO && balance.current_period_amount == null) {
    return {
      status: 'unconfigured',
      amount: '0.00',
      headline: 'No recurring dues are set up for this home yet.',
      owes: false,
      daysPastDue: 0,
    };
  }

  // Rule 2 — the association is holding money for them.
  if (isNegativeMoney(balance.balance)) {
    return {
      status: 'credit',
      amount: absMoney(balance.balance),
      headline: 'Credit on your account',
      owes: false,
      daysPastDue: 0,
    };
  }

  if (cents === BIG_ZERO) {
    return {
      status: 'paid_up',
      amount: '0.00',
      headline: "You're all paid up",
      owes: false,
      daysPastDue: 0,
    };
  }

  // Rule 3 — overdue is what the backend says it is.
  if (daysPastDue > 0) {
    return {
      status: 'past_due',
      amount: balance.balance,
      headline:
        daysPastDue === 1
          ? 'Past due by 1 day'
          : `Past due by ${daysPastDue} days`,
      owes: true,
      daysPastDue,
    };
  }

  return {
    status: 'due',
    amount: balance.balance,
    headline: 'Balance due',
    owes: true,
    daysPastDue: 0,
  };
};

/**
 * Tailwind classes for the hero's figure.
 *
 * Past due is the only state that gets a warning colour. A balance that
 * is simply due is not a problem, and colouring it red teaches people
 * to ignore red.
 */
export const accountAmountClasses = (status: AccountStatus): string => {
  switch (status) {
    case 'past_due':
      return 'text-red-700 dark:text-red-400';
    case 'credit':
      return 'text-lime-700 dark:text-lime-400';
    default:
      return 'text-zinc-900 dark:text-white';
  }
};
